import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type AltaMiembro, type Fiduciante, type Miembro, type PasswordTemporal, type Rol } from "@/lib/api";
import { fichasLibres, opcionesFichas, temporalGenerada, type TemporalGenerada } from "@/lib/usuarios";

type Dialogo =
  | { tipo: "alta" }
  | { tipo: "rol"; miembro: Miembro; rol: Rol; fichaId: number | null }
  | { tipo: "vincular"; miembro: Miembro; fichaId: number | null }
  | { tipo: "confirmar-temporal"; miembro: Miembro }
  | { tipo: "temporal"; datos: TemporalGenerada }
  | { tipo: "activo"; miembro: Miembro }
  | null;

const ALTA_VACIA = { nombre: "", email: "", rol: "" as Rol | "", fichaId: null as number | null };

/** Lógica de Configuración → Usuarios (sin JSX). */
export function useUsuariosTab() {
  const { fid, usuario: yo } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/usuarios`;
  const lista = useApiResource<Miembro[]>(base);
  // Las fichas sin usuario: un usuario con rol fiduciante se vincula siempre a una.
  const fiduciantes = useApiResource<Fiduciante[]>(`/api/fideicomisos/${fid}/fiduciantes`);
  const libres = fichasLibres(fiduciantes.data ?? []);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [alta, setAlta] = useState(ALTA_VACIA);
  const [resultadoAlta, setResultadoAlta] = useState<{ nombre: string; temporal: TemporalGenerada | null } | null>(null);

  const accionAlta = useAsyncAction((d: { nombre: string; email: string; rol: Rol; fiduciante_id?: number }) => api.post<AltaMiembro>(base, d));
  const accionEditar = useAsyncAction((uid: number, cambios: { rol?: Rol; activo?: boolean; fiduciante_id?: number }) =>
    api.patch<Miembro>(`${base}/${uid}`, cambios),
  );
  const accionTemporal = useAsyncAction((uid: number) => api.post<PasswordTemporal>(`${base}/${uid}/password-temporal`));

  const cerrar = () => setDialogo(null);
  const recargarTodo = () => {
    lista.recargar();
    fiduciantes.recargar();
  };
  const nombreFicha = (id: number | null) => (id === null ? null : (libres.find((f) => f.id === id)?.nombre ?? null));

  const abrirAlta = () => {
    setAlta(ALTA_VACIA);
    setResultadoAlta(null);
    accionAlta.limpiarError();
    setDialogo({ tipo: "alta" });
  };

  const cambiarAlta = (campo: "nombre" | "email" | "rol", valor: string) => setAlta((a) => ({ ...a, [campo]: valor }));

  /** Al elegir la ficha, nombre y email salen de ella (el email se deja si la ficha no tiene). */
  const elegirFichaAlta = (valor: string) => {
    const ficha = libres.find((f) => String(f.id) === valor);
    if (ficha) setAlta((a) => ({ ...a, fichaId: ficha.id, nombre: ficha.nombre, email: ficha.email ?? a.email }));
  };
  const soltarFichaAlta = () => setAlta((a) => ({ ...a, fichaId: null }));

  const faltaFichaAlta = alta.rol === "fiduciante" && alta.fichaId === null;
  const puedeCrear = alta.nombre.trim() !== "" && alta.email.trim() !== "" && alta.rol !== "" && !faltaFichaAlta && !accionAlta.loading;

  const crear = async (e: FormEvent) => {
    e.preventDefault();
    if (!puedeCrear || alta.rol === "") return;
    const datos = { nombre: alta.nombre.trim(), email: alta.email.trim(), rol: alta.rol };
    const r = await accionAlta.run(alta.rol === "fiduciante" && alta.fichaId !== null ? { ...datos, fiduciante_id: alta.fichaId } : datos);
    if (r) {
      recargarTodo();
      setResultadoAlta({ nombre: r.usuario.nombre, temporal: r.password_temporal ? temporalGenerada(r.usuario.nombre, r.password_temporal) : null });
    }
  };

  const abrirRol = (miembro: Miembro) => {
    accionEditar.limpiarError();
    setDialogo({ tipo: "rol", miembro, rol: miembro.rol, fichaId: null });
  };
  const elegirRol = (rol: Rol) => setDialogo((d) => (d?.tipo === "rol" ? { ...d, rol } : d));
  /** Pasar a fiduciante a quien no tiene ficha pide elegirla (el backend da 409 sin ella). */
  const rolPideFicha = dialogo?.tipo === "rol" && dialogo.rol === "fiduciante" && dialogo.miembro.rol !== "fiduciante" && dialogo.miembro.fiduciante === null;
  const guardarRol = async () => {
    if (dialogo?.tipo !== "rol") return;
    if (rolPideFicha && dialogo.fichaId === null) return;
    const cambios = rolPideFicha && dialogo.fichaId !== null ? { rol: dialogo.rol, fiduciante_id: dialogo.fichaId } : { rol: dialogo.rol };
    if (await accionEditar.run(dialogo.miembro.id, cambios)) {
      cerrar();
      recargarTodo();
    }
  };

  const abrirVincular = (miembro: Miembro) => {
    accionEditar.limpiarError();
    setDialogo({ tipo: "vincular", miembro, fichaId: null });
  };
  const guardarVincular = async () => {
    if (dialogo?.tipo !== "vincular" || dialogo.fichaId === null) return;
    if (await accionEditar.run(dialogo.miembro.id, { fiduciante_id: dialogo.fichaId })) {
      cerrar();
      recargarTodo();
    }
  };

  /** Ficha elegida en los diálogos de Rol y Vincular. */
  const elegirFichaDialogo = (valor: string) => {
    const id = Number(valor);
    setDialogo((d) => (d?.tipo === "rol" || d?.tipo === "vincular" ? { ...d, fichaId: id } : d));
  };
  const soltarFichaDialogo = () => setDialogo((d) => (d?.tipo === "rol" || d?.tipo === "vincular" ? { ...d, fichaId: null } : d));

  const abrirActivo = (miembro: Miembro) => {
    accionEditar.limpiarError();
    setDialogo({ tipo: "activo", miembro });
  };
  const confirmarActivo = async () => {
    if (dialogo?.tipo !== "activo") return;
    if (await accionEditar.run(dialogo.miembro.id, { activo: !dialogo.miembro.activo })) {
      cerrar();
      lista.recargar();
    }
  };

  const abrirTemporal = (miembro: Miembro) => {
    accionTemporal.limpiarError();
    setDialogo({ tipo: "confirmar-temporal", miembro });
  };
  const generarTemporal = async () => {
    if (dialogo?.tipo !== "confirmar-temporal") return;
    const { miembro } = dialogo;
    const t = await accionTemporal.run(miembro.id);
    if (t) {
      setDialogo({ tipo: "temporal", datos: temporalGenerada(miembro.nombre, t) });
      lista.recargar();
    }
  };

  return {
    miembros: lista.data ?? [],
    cargando: lista.loading && !lista.data,
    errorCarga: lista.data ? null : (lista.error?.message ?? null),
    reintentar: lista.recargar,
    esYo: (m: Miembro) => m.id === yo.id,
    dialogo,
    cerrar,
    opcionesFichas: opcionesFichas(libres),
    /** Sin fichas libres no hay a quién vincular: "Cargalo primero en Fiduciantes". */
    sinFichasLibres: fiduciantes.data !== undefined && libres.length === 0,
    errorFichas: fiduciantes.error?.message ?? null,
    nombreFicha,
    alta,
    cambiarAlta,
    elegirFichaAlta,
    soltarFichaAlta,
    abrirAlta,
    puedeCrear,
    crear,
    creando: accionAlta.loading,
    errorAlta: accionAlta.error,
    resultadoAlta,
    abrirRol,
    elegirRol,
    rolPideFicha,
    guardarRol,
    abrirVincular,
    guardarVincular,
    elegirFichaDialogo,
    soltarFichaDialogo,
    abrirActivo,
    confirmarActivo,
    guardando: accionEditar.loading,
    errorEditar: accionEditar.error,
    abrirTemporal,
    generarTemporal,
    generando: accionTemporal.loading,
    errorTemporal: accionTemporal.error,
  };
}
